import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Chip,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Sync as SyncIcon,
  Bloodtype as DiabetesIcon,
  MonitorWeight as WeightIcon,
  Favorite as HypertensionIcon,
} from "@mui/icons-material";
import {
  patientService,
  PatientProvider,
  ProviderSearchResult,
  CCMCareProvider,
} from "../../services/patient.service";

interface Props {
  patientId: number;
}

const PatientProvidersTab: React.FC<Props> = ({ patientId }) => {
  // Program entity types that support program enrollment and termination
  const isProgramEntity = (entityTypeID: number | undefined): boolean => {
    if (!entityTypeID) return false;
    return [5, 6, 7, 9, 10, 11, 12, 13, 14, 15].includes(entityTypeID);
  };

  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState<PatientProvider[]>([]);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [providerToDelete, setProviderToDelete] =
    useState<PatientProvider | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<ProviderSearchResult[]>(
    [],
  );
  const [selectedProvider, setSelectedProvider] =
    useState<ProviderSearchResult | null>(null);
  const [editingProvider, setEditingProvider] =
    useState<PatientProvider | null>(null);
  const [newProvider, setNewProvider] = useState({
    accountID: 0,
    entityID: 0,
    careProvider: "",
    externalLookup: "",
    diabetesProgram: 0,
    weightProgram: 0,
    hypertensionProgram: 0,
  });
  const [editData, setEditData] = useState({
    externalLookup: "",
    careProvider: "",
    diabetesProgram: 0,
    weightProgram: 0,
    hypertensionProgram: 0,
  });

  // CCM Care Provider state for edit modal
  const [ccmCareProviders, setCcmCareProviders] = useState<CCMCareProvider[]>(
    [],
  );
  const [assignedCcmCareProviders, setAssignedCcmCareProviders] = useState<
    CCMCareProvider[]
  >([]);
  const [loadingCcm, setLoadingCcm] = useState(false);
  const [showCcmSelection, setShowCcmSelection] = useState(false);
  const [selectedCcmProviders, setSelectedCcmProviders] = useState<string[]>(
    [],
  );
  const [ccmSuccess, setCcmSuccess] = useState("");
  const [syncingEntityId, setSyncingEntityId] = useState<number | null>(null);
  const [syncToast, setSyncToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [syncError, setSyncError] = useState<{
    entityId: number;
    message: string;
  } | null>(null);

  useEffect(() => {
    loadProviders();
  }, [patientId]);

  const loadProviders = async () => {
    try {
      setLoading(true);
      const data = await patientService.getPatientProviders(patientId);
      setProviders(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load providers");
    } finally {
      setLoading(false);
    }
  };

  const searchProviders = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const results = await patientService.searchProviders(searchTerm);
      setSearchResults(results);
    } catch (err: any) {
      console.error("Search error:", err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleDeleteClick = (provider: PatientProvider) => {
    setProviderToDelete(provider);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!providerToDelete) return;

    try {
      await patientService.removePatientProvider(
        patientId,
        providerToDelete.accountID,
      );
      setOpenDeleteDialog(false);
      setProviderToDelete(null);
      loadProviders();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove provider");
      setOpenDeleteDialog(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedProvider) {
      setError("Please select a provider");
      return;
    }

    try {
      await patientService.addPatientProvider(patientId, {
        accountID: selectedProvider.primaryAccountID || 0,
        entityID: selectedProvider.entityID,
        careProvider: newProvider.careProvider,
        externalLookup: newProvider.externalLookup,
        diabetesProgram: newProvider.diabetesProgram,
        weightProgram: newProvider.weightProgram,
        hypertensionProgram: newProvider.hypertensionProgram,
      });
      setOpenDialog(false);
      setSelectedProvider(null);
      setNewProvider({
        accountID: 0,
        entityID: 0,
        careProvider: "",
        externalLookup: "",
        diabetesProgram: 0,
        weightProgram: 0,
        hypertensionProgram: 0,
      });
      setSearchResults([]);
      loadProviders();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add provider");
    }
  };

  const handleEdit = async (provider: PatientProvider) => {
    setEditingProvider(provider);
    setEditData({
      externalLookup: provider.externalLookup || "",
      careProvider: provider.careProvider || "",
      diabetesProgram: provider.diabetesProgram || 0,
      weightProgram: provider.weightProgram || 0,
      hypertensionProgram: provider.hypertensionProgram || 0,
    });
    setCcmSuccess("");
    setShowCcmSelection(false);
    setSelectedCcmProviders([]);

    // Load CCM care providers if this entity has Eocene integration
    const isCcmEnabled = [983, 1008, 2016].includes(provider.entityID);
    if (isCcmEnabled) {
      loadCcmCareProvidersForEdit(provider.entityID);
    } else {
      setCcmCareProviders([]);
      setAssignedCcmCareProviders([]);
    }

    setOpenEditDialog(true);
  };

  const loadCcmCareProvidersForEdit = async (entityId: number) => {
    try {
      setLoadingCcm(true);
      const [available, assigned] = await Promise.all([
        patientService.getCCMCareProviders(entityId),
        patientService.getPatientCCMCareProviders(patientId, entityId),
      ]);
      setCcmCareProviders(available);
      setAssignedCcmCareProviders(assigned);
    } catch (err: any) {
      console.error("Failed to load CCM care providers:", err);
    } finally {
      setLoadingCcm(false);
    }
  };

  const handleToggleCcmProvider = (providerId: string) => {
    setSelectedCcmProviders((prev) => {
      if (prev.includes(providerId)) {
        return prev.filter((id) => id !== providerId);
      } else {
        return [...prev, providerId];
      }
    });
  };

  const handleAssignCcmProviders = async () => {
    if (!editingProvider || selectedCcmProviders.length === 0) return;

    try {
      setLoadingCcm(true);
      const result = await patientService.assignPatientToCCMCareProvider(
        patientId,
        editingProvider.entityID,
        selectedCcmProviders,
      );

      const allSuccess = result.results.every((r) => r.success);
      if (allSuccess) {
        setCcmSuccess(
          `Successfully assigned ${selectedCcmProviders.length} care provider(s)`,
        );
        setShowCcmSelection(false);
        setSelectedCcmProviders([]);
        // Reload assigned care providers
        loadCcmCareProvidersForEdit(editingProvider.entityID);
      } else {
        const failures = result.results.filter((r) => !r.success);
        setError(
          `Some assignments failed: ${failures.map((f) => f.error).join(", ")}`,
        );
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to assign CCM care providers",
      );
    } finally {
      setLoadingCcm(false);
    }
  };

  const handleSyncToCCM = async (provider: PatientProvider) => {
    setSyncingEntityId(provider.entityID);
    setSyncError(null);
    try {
      const result = await patientService.importPatientToCCM(
        patientId,
        provider.entityID,
      );
      if (result.success) {
        const msg = result.wasExisting
          ? "Patient already existed in CCM" +
            (result.linked === false
              ? ` (care provider link failed: ${result.linkError})`
              : "")
          : "Patient synced to CCM successfully" +
            (result.linked === false
              ? ` (care provider link failed: ${result.linkError})`
              : "");
        setSyncToast({ open: true, message: msg, severity: "success" });
        loadProviders();
      } else {
        setSyncError({
          entityId: provider.entityID,
          message: result.partnerError || "Sync failed",
        });
      }
    } catch (err: any) {
      setSyncError({
        entityId: provider.entityID,
        message: err.response?.data?.error || err.message || "Sync failed",
      });
    } finally {
      setSyncingEntityId(null);
    }
  };

  const handleUpdate = async () => {
    if (!editingProvider) return;

    try {
      await patientService.updatePatientProvider(
        patientId,
        editingProvider.accountID,
        editData,
      );
      setOpenEditDialog(false);
      setEditingProvider(null);
      loadProviders();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update provider");
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={2}
      >
        <Typography variant='h6'>Care Providers</Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Provider
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <List sx={{ padding: 0 }}>
        {providers.map((provider, index) => {
          const accountAddress = [
            provider.accountAddress1,
            provider.accountAddress2,
            provider.accountCity,
            provider.accountState,
            provider.accountZipcode,
          ]
            .filter(Boolean)
            .join(", ");

          const entityAddress = [
            provider.entityAddress1,
            provider.entityAddress2,
            provider.entityCity,
            provider.entityState,
            provider.entityZipcode,
          ]
            .filter(Boolean)
            .join(", ");

          return (
            <Paper
              key={provider.accountID}
              elevation={1}
              sx={{
                mb: 2,
                p: 2,
                "&:hover": {
                  elevation: 3,
                  boxShadow: 3,
                },
              }}
            >
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='flex-start'
              >
                <Box flex={1}>
                  {/* Entity Name as primary heading with Managing Provider badge */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant='h6' color='primary'>
                      {provider.entityName || "Unknown Entity"}
                    </Typography>
                    {provider.managing && (
                      <Chip
                        icon={<span>⭐</span>}
                        label='Managing Provider'
                        size='small'
                        color='primary'
                        variant='filled'
                      />
                    )}
                  </Box>

                  {/* Entity Type */}
                  {provider.entityType && (
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      {provider.entityType}
                    </Typography>
                  )}

                  {/* Program Enrollment Indicators */}
                  {isProgramEntity(provider.entityTypeID) &&
                    (provider.diabetesProgram ||
                      provider.weightProgram ||
                      provider.hypertensionProgram) && (
                      <Box sx={{ display: "flex", gap: 1, mt: 0.5, mb: 1 }}>
                        {provider.diabetesProgram && (
                          <Chip
                            icon={<DiabetesIcon />}
                            label='Diabetes'
                            size='small'
                            sx={{
                              bgcolor: "#0393b8",
                              color: "white",
                              "& .MuiChip-icon": { color: "white" },
                            }}
                          />
                        )}
                        {provider.weightProgram && (
                          <Chip
                            icon={<WeightIcon />}
                            label='Weight'
                            size='small'
                            sx={{
                              bgcolor: "#569a45",
                              color: "white",
                              "& .MuiChip-icon": { color: "white" },
                            }}
                          />
                        )}
                        {provider.hypertensionProgram && (
                          <Chip
                            icon={<HypertensionIcon />}
                            label='Hypertension'
                            size='small'
                            sx={{
                              bgcolor: "#c73e2e",
                              color: "white",
                              "& .MuiChip-icon": { color: "white" },
                            }}
                          />
                        )}
                      </Box>
                    )}

                  <Box sx={{ display: "grid", gap: 0.5, mt: 1.5 }}>
                    {/* Account Name and Title */}
                    {(provider.firstname || provider.lastname) && (
                      <Typography variant='body2'>
                        {provider.firstname} {provider.lastname}
                        {provider.title && `, ${provider.title}`}
                      </Typography>
                    )}

                    {/* Care Provider Name - Only show for non-CCM entities (legacy field) */}
                    {provider.careProvider &&
                      ![983, 1008, 2016].includes(provider.entityID) && (
                        <Typography variant='body2'>
                          <strong>Care Provider:</strong>{" "}
                          {provider.careProvider}
                        </Typography>
                      )}

                    {/* External ID */}
                    {provider.externalLookup && (
                      <Typography variant='body2'>
                        <strong>External ID:</strong> {provider.externalLookup}
                      </Typography>
                    )}

                    {/* CCM System ID */}
                    {provider.externalSystemId && (
                      <Typography variant='body2'>
                        <strong>CCM ID:</strong>{" "}
                        <Box
                          component='span'
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                            bgcolor: "grey.100",
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                          }}
                        >
                          {provider.externalSystemId}
                        </Box>
                      </Typography>
                    )}

                    {/* Address - prefer entity address, fallback to account */}
                    {(entityAddress || accountAddress) && (
                      <Typography variant='body2' sx={{ mt: 0.5 }}>
                        {entityAddress || accountAddress}
                      </Typography>
                    )}

                    {/* Phone - prefer entity phone, fallback to account */}
                    {(provider.entityPhone || provider.accountPhone) && (
                      <Typography variant='body2'>
                        P: {provider.entityPhone || provider.accountPhone}
                      </Typography>
                    )}

                    {/* Email if available */}
                    {provider.emailAddress && (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 0.5 }}
                      >
                        {provider.emailAddress}
                      </Typography>
                    )}

                    {/* Provider IDs at bottom */}
                    <Box
                      sx={{
                        mt: 1,
                        pt: 1,
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant='caption' color='text.secondary'>
                        Account ID: {provider.accountID} | Entity ID:{" "}
                        {provider.entityID}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box display='flex' gap={1} alignItems='flex-start'>
                  {[983, 1008].includes(provider.entityID) && (
                    <Tooltip title='Sync to CCM'>
                      <span>
                        <IconButton
                          color='info'
                          onClick={() => handleSyncToCCM(provider)}
                          size='small'
                          disabled={syncingEntityId === provider.entityID}
                        >
                          {syncingEntityId === provider.entityID ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SyncIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <IconButton
                    color='primary'
                    onClick={() => handleEdit(provider)}
                    size='small'
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color={
                      isProgramEntity(provider.entityTypeID)
                        ? "warning"
                        : "error"
                    }
                    onClick={() => handleDeleteClick(provider)}
                    size='small'
                    title={
                      isProgramEntity(provider.entityTypeID)
                        ? "Terminate from Program"
                        : "Remove Provider"
                    }
                  >
                    <CancelIcon />
                  </IconButton>
                </Box>
              </Box>
              {syncError?.entityId === provider.entityID && (
                <Alert
                  severity='error'
                  sx={{ mt: 1 }}
                  onClose={() => setSyncError(null)}
                >
                  CCM Sync failed: {syncError.message}
                </Alert>
              )}
            </Paper>
          );
        })}
        {providers.length === 0 && (
          <Paper elevation={1} sx={{ p: 3, textAlign: "center" }}>
            <Typography color='text.secondary'>
              No providers assigned
            </Typography>
          </Paper>
        )}
      </List>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Add Provider</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              options={searchResults}
              getOptionLabel={(option) =>
                `${option.entityName}${option.primaryFirstname ? ` - ${option.primaryFirstname} ${option.primaryLastname}` : ""}${option.entityType ? ` (${option.entityType})` : ""}`
              }
              loading={searchLoading}
              value={selectedProvider}
              onChange={(event, newValue) => {
                setSelectedProvider(newValue);
                if (newValue) {
                  setNewProvider({
                    ...newProvider,
                    accountID: newValue.primaryAccountID || 0,
                    entityID: newValue.entityID || 0,
                  });
                }
              }}
              onInputChange={(event, newInputValue) => {
                searchProviders(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Search Provider'
                  placeholder='Type entity or name to search...'
                  helperText='Search by entity name or contact person'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant='body1' fontWeight='bold'>
                      {option.entityName}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {option.entityType}
                      {option.primaryFirstname &&
                        ` - ${option.primaryFirstname} ${option.primaryLastname}`}
                      {option.primaryTitle && `, ${option.primaryTitle}`}
                    </Typography>
                  </Box>
                </li>
              )}
            />

            {selectedProvider && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant='subtitle2'
                  color='text.secondary'
                  gutterBottom
                >
                  Primary Contact
                </Typography>
                <TextField
                  margin='dense'
                  label='Account Contact'
                  fullWidth
                  value={
                    `${selectedProvider.primaryFirstname || ""} ${selectedProvider.primaryLastname || ""}`.trim() ||
                    "No primary contact"
                  }
                  disabled
                  helperText='Primary account contact for this entity'
                />
                <TextField
                  margin='dense'
                  label='External ID'
                  fullWidth
                  value={newProvider.externalLookup}
                  placeholder="Patient's ID in provider's system"
                  helperText="The patient's ID as it appears in this provider's system"
                  onChange={(e) =>
                    setNewProvider({
                      ...newProvider,
                      externalLookup: e.target.value,
                    })
                  }
                />

                {/* Program flags for Program Entity Types */}
                {isProgramEntity(selectedProvider.entityTypeID) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Programs
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!newProvider.diabetesProgram}
                          onChange={(e) =>
                            setNewProvider({
                              ...newProvider,
                              diabetesProgram: e.target.checked ? 1 : 0,
                            })
                          }
                        />
                      }
                      label='Diabetes Program'
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!newProvider.weightProgram}
                          onChange={(e) =>
                            setNewProvider({
                              ...newProvider,
                              weightProgram: e.target.checked ? 1 : 0,
                            })
                          }
                        />
                      }
                      label='Weight Management Program'
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!newProvider.hypertensionProgram}
                          onChange={(e) =>
                            setNewProvider({
                              ...newProvider,
                              hypertensionProgram: e.target.checked ? 1 : 0,
                            })
                          }
                        />
                      }
                      label='Hypertension Program'
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setSelectedProvider(null);
              setSearchResults([]);
              setNewProvider({
                accountID: 0,
                entityID: 0,
                careProvider: "",
                externalLookup: "",
                diabetesProgram: 0,
                weightProgram: 0,
                hypertensionProgram: 0,
              });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant='contained'
            disabled={!selectedProvider}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Edit Provider Information</DialogTitle>
        <DialogContent>
          {editingProvider && (
            <Box sx={{ mt: 1 }}>
              <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
                {editingProvider.entityName}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                gutterBottom
                sx={{ mb: 2 }}
              >
                {editingProvider.firstname} {editingProvider.lastname}
              </Typography>

              <TextField
                margin='dense'
                label='External ID'
                fullWidth
                value={editData.externalLookup}
                helperText="The patient's ID in this provider's system"
                onChange={(e) =>
                  setEditData({ ...editData, externalLookup: e.target.value })
                }
              />

              {/* Program flags for Program Entity Types */}
              {isProgramEntity(editingProvider.entityTypeID) && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant='subtitle2'
                    color='text.secondary'
                    gutterBottom
                  >
                    Programs
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!editData.diabetesProgram}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            diabetesProgram: e.target.checked ? 1 : 0,
                          })
                        }
                      />
                    }
                    label='Diabetes Program'
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!editData.weightProgram}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            weightProgram: e.target.checked ? 1 : 0,
                          })
                        }
                      />
                    }
                    label='Weight Management Program'
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!editData.hypertensionProgram}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            hypertensionProgram: e.target.checked ? 1 : 0,
                          })
                        }
                      />
                    }
                    label='Hypertension Program'
                  />
                </Box>
              )}

              {/* CCM Care Provider Assignment Section */}
              {editingProvider &&
                [983, 1008, 2016].includes(editingProvider.entityID) && (
                  <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant='subtitle2' gutterBottom>
                      CCM Care Provider Assignments
                    </Typography>

                    {ccmSuccess && (
                      <Alert
                        severity='success'
                        sx={{ mb: 2 }}
                        onClose={() => setCcmSuccess("")}
                      >
                        {ccmSuccess}
                      </Alert>
                    )}

                    {loadingCcm ? (
                      <Box display='flex' justifyContent='center' p={2}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <>
                        {/* Currently Assigned Care Providers */}
                        {assignedCcmCareProviders.length > 0 ? (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              Currently Assigned:
                            </Typography>
                            <List dense>
                              {assignedCcmCareProviders.map((cp) => (
                                <ListItem key={cp._id} sx={{ py: 0.5, px: 1 }}>
                                  <ListItemText
                                    primary={`${cp.fname} ${cp.lname}`}
                                    secondary={cp.email}
                                    primaryTypographyProps={{
                                      variant: "body2",
                                    }}
                                    secondaryTypographyProps={{
                                      variant: "caption",
                                    }}
                                  />
                                  {cp.isActive && (
                                    <Chip
                                      label='Active'
                                      size='small'
                                      color='success'
                                    />
                                  )}
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        ) : (
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ mb: 2 }}
                          >
                            No CCM care providers assigned yet
                          </Typography>
                        )}

                        {/* Add Care Provider Button */}
                        {!showCcmSelection && (
                          <Button
                            size='small'
                            startIcon={<AddIcon />}
                            onClick={() => setShowCcmSelection(true)}
                          >
                            Add Care Provider
                          </Button>
                        )}

                        {/* Care Provider Selection UI */}
                        {showCcmSelection && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              bgcolor: "background.default",
                              borderRadius: 1,
                            }}
                          >
                            <Typography variant='body2' gutterBottom>
                              Select care providers to assign:
                            </Typography>
                            <Autocomplete
                              multiple
                              options={ccmCareProviders}
                              getOptionLabel={(option) =>
                                `${option.fname} ${option.lname}`
                              }
                              value={ccmCareProviders.filter((cp) =>
                                selectedCcmProviders.includes(cp._id),
                              )}
                              onChange={(_, newValue) => {
                                setSelectedCcmProviders(
                                  newValue.map((cp) => cp._id),
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant='outlined'
                                  size='small'
                                  label='Search and select care providers'
                                  placeholder='Type to search...'
                                />
                              )}
                              renderOption={(props, option) => (
                                <li {...props}>
                                  <Box sx={{ width: "100%" }}>
                                    <Typography variant='body2'>
                                      {option.fname} {option.lname}
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                    >
                                      {option.email || option.username}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    {...getTagProps({ index })}
                                    label={`${option.fname} ${option.lname}`}
                                    size='small'
                                  />
                                ))
                              }
                              sx={{ mb: 2 }}
                            />
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                size='small'
                                onClick={() => {
                                  setShowCcmSelection(false);
                                  setSelectedCcmProviders([]);
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size='small'
                                variant='contained'
                                onClick={handleAssignCcmProviders}
                                disabled={
                                  selectedCcmProviders.length === 0 ||
                                  loadingCcm
                                }
                              >
                                Assign{" "}
                                {selectedCcmProviders.length > 0 &&
                                  `(${selectedCcmProviders.length})`}
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant='contained'>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Provider Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {isProgramEntity(providerToDelete?.entityTypeID)
            ? "Terminate from Program"
            : "Remove Provider"}
        </DialogTitle>
        <DialogContent>
          {isProgramEntity(providerToDelete?.entityTypeID)
            ? providerToDelete && (
                <Box>
                  <Alert severity='warning' sx={{ mb: 2 }}>
                    This will terminate the patient from the program.
                  </Alert>
                  <Typography variant='body2'>
                    Are you sure you want to remove{" "}
                    <strong>{providerToDelete.entityName}</strong>?
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ mt: 2, fontStyle: "italic", color: "text.secondary" }}
                  >
                    A termination record will be created for audit purposes.
                  </Typography>
                </Box>
              )
            : providerToDelete && (
                <Typography variant='body2'>
                  Are you sure you want to remove{" "}
                  <strong>{providerToDelete.entityName}</strong> from the
                  patient's care team?
                </Typography>
              )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant='contained'
            color={
              isProgramEntity(providerToDelete?.entityTypeID)
                ? "warning"
                : "error"
            }
          >
            {isProgramEntity(providerToDelete?.entityTypeID)
              ? "Terminate"
              : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={syncToast.open}
        autoHideDuration={4000}
        onClose={() => setSyncToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSyncToast((t) => ({ ...t, open: false }))}
          severity={syncToast.severity}
          sx={{ width: "100%" }}
        >
          {syncToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientProvidersTab;
